﻿import { createFileRoute } from "@tanstack/react-router";
import type { Id } from "@convex/_generated/dataModel";
import { useLeague } from "@/features/leagues/hooks/useLeague";
import { useCurrentUser } from "@/features/auth/hooks/useCurrentUser";
import { useLeagueStandings } from "@/features/leagues/standings/hooks/useLeagueStandings";
import { LeagueStandingsTable } from "@/features/leagues/standings/components/LeagueStandingsTable";
import { PageContainer } from "@/shared/layout/PageContainer";
import { PageHeader } from "@/shared/layout/PageHeader";
import { Section } from "@/shared/layout/Section";
import { Skeleton } from "@/shared/ui/Skeleton";
import { EmptyState } from "@/shared/ui/EmptyState";
import { useT } from "@/shared/i18n/LocaleContext";

export const Route = createFileRoute("/_authenticated/leagues/$leagueId/standings")({
  component: LeagueStandingsPage,
});

function LeagueStandingsPage() {
  const { leagueId } = Route.useParams();
  const t = useT();
  const data = useLeague(leagueId as Id<"leagues">);
  const me = useCurrentUser();
  const standings = useLeagueStandings(leagueId as Id<"leagues">);

  if (data === undefined || standings === undefined) {
    return (
      <PageContainer>
        <Skeleton className="h-12 w-2/3 mb-8" />
        <Skeleton className="h-64 w-full mb-6" />
        <Skeleton className="h-40 w-full mb-6" />
        <Skeleton className="h-80 w-full" />
      </PageContainer>
    );
  }

  const { league, members } = data;
  const myMembership = members.find((m) => m.userId === me?._id);
  const myMemberId = myMembership?._id ?? null;

  return (
    <PageContainer>
      <PageHeader
        eyebrow={league.name}
        title={t("league.standings.title")}
      />

      {standings.regular.length === 0 ? (
        <EmptyState
          eyebrow={t("league.standings.title")}
          title={t("league.standings.emptyTitle")}
          description={t("league.standings.empty")}
        />
      ) : (
        <div className="flex flex-col gap-6">
          {league.state === "complete"
            ? (() => {
                const champion = standings.regular[0];
                return champion ? (
                  <div
                    className="glow-card rounded-md border bg-accent-soft/30 p-4"
                    data-testid="banner-champion"
                  >
                    <p className="text-label text-accent mb-1">
                      {t("league.standings.champion")}
                    </p>
                    <p
                      className="font-display text-lg font-semibold text-ink"
                      data-testid="champion-team-name"
                    >
                      {champion.teamName}
                    </p>
                  </div>
                ) : null;
              })()
            : null}
          <Section>
            <LeagueStandingsTable
              leagueId={leagueId as Id<"leagues">}
              rows={standings.regular}
              myMemberId={myMemberId}
            />
          </Section>
        </div>
      )}
    </PageContainer>
  );
}
